"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import logoPng from "@/app/owm_logo.png";
import meshLogoPng from "@/app/meshlogo.png";
import { usePathname } from "next/navigation";
import { useSidebar } from "../context/SidebarContext";
import { getGatewayBaseUrl } from "@/lib/gateway";
import {
  BoxCubeIcon,
  ChevronDownIcon,
  GroupIcon,
  HorizontaLDots,
  MoyskladIcon,
  OzonIcon,
  PlugInIcon,
  WbIcon,
  YandexIcon,
} from "../icons/index";

type SubItem = {
  name: string;
  path?: string;
  pro?: boolean;
  new?: boolean;
  children?: SubItem[];
};

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubItem[];
  badgeCount?: number;
};

type PluginMeta = {
  name: string;
  enabled: boolean;
};

type AccessCheckResponse = {
  allowed: boolean;
};

function getToken(): string {
  const raw = (window as any).__hubcrmAccessToken;
  if (!raw) return "";
  const token = String(raw).trim();
  if (!token || token === "undefined" || token === "null") return "";
  return token;
}

function parseJwtPayload(token: string): any {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4 ? "=".repeat(4 - (payload.length % 4)) : "";
    return JSON.parse(atob(payload + pad));
  } catch {
    return null;
  }
}

function hasAdminAccess(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload) return false;
  const realmRoles = Array.isArray(payload?.realm_access?.roles) ? payload.realm_access.roles : [];
  if (realmRoles.includes("superadmin") || realmRoles.includes("admin")) return true;
  const resourceAccess = payload?.resource_access || {};
  return Object.values(resourceAccess).some(
    (obj: any) => Array.isArray(obj?.roles) && (obj.roles.includes("superadmin") || obj.roles.includes("admin"))
  );
}

const othersItems: NavItem[] = [];

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const pathname = usePathname();
  const [openSubItemChildren, setOpenSubItemChildren] = useState<Record<string, boolean>>({});
  const [enabledModuleOrder, setEnabledModuleOrder] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);

  const navItems = React.useMemo<NavItem[]>(() => {
    const dynamicItems: NavItem[] = [];
    const knownModuleItems: Record<string, NavItem> = {
      marketplaces: {
        name: "Маркетплейсы",
        icon: <BoxCubeIcon />,
        subItems: [
          {
            name: "Настройки",
            children: [
              { name: "Общие", path: "/modules/marketplaces/settings/common" },
              { name: "Принтеры", path: "/modules/marketplaces/settings/printers" },
              { name: "Синхронизация остатков", path: "/modules/marketplaces/settings/stock-sync" },
              { name: "Исключённые артикулы", path: "/modules/marketplaces/settings/offer-exclusions" },
            ],
          },
          { name: "Габариты", path: "/modules/marketplaces/dimensions" },
          { name: "ШтрихКоды", path: "/modules/marketplaces/barcodes" },
          { name: "Копирование", path: "/modules/marketplaces/copying" },
          { name: "Опт/Закупка", path: "/modules/marketplaces/purchase" },
          { name: "Сравнение", path: "/modules/marketplaces/compare" },
        ],
      },
      ozon: {
        name: "Ozon",
        icon: <OzonIcon />,
        subItems: [
          { name: "Настройки", path: "/modules/marketplaces/ozon/settings" },
          { name: "Заказы FBS", path: "/modules/marketplaces/fbs/orders" },
          {
            name: "Финансы",
            children: [
              { name: "Отчеты", path: "/modules/marketplaces/ozon/finances" },
              { name: "Статистика", path: "/modules/marketplaces/ozon/finances/statistics" },
            ],
          },
          { name: "Акции", path: "/modules/marketplaces/ozon/promotions" },
          { name: "Лента заказов", path: "/modules/marketplaces/ozon/orders-feed" },
          {
            name: "FBY",
            children: [
              { name: "Поставки", path: "/modules/marketplaces/ozon/fby/supply" },
              { name: "Настройки", path: "/modules/marketplaces/ozon/fby/settings" },
            ],
          },
        ],
      },
      wb: {
        name: "WB",
        icon: <WbIcon />,
        subItems: [
          { name: "Настройки", path: "/modules/marketplaces/wb/settings" },
          { name: "Заказы FBS", path: "/modules/marketplaces/fbs/orders" },
          { name: "Финансы", path: "/modules/marketplaces/wb/finances" },
          { name: "Лента заказов", path: "/modules/marketplaces/wb/orders-feed" },
          { name: "Цены и скидки", path: "/modules/marketplaces/wb/promotions" },
          {
            name: "FBY",
            children: [
              { name: "Остатки", path: "/modules/marketplaces/wb/fby/stocks" },
              { name: "Поставки", path: "/modules/marketplaces/wb/fby/supply" },
              { name: "Настройки", path: "/modules/marketplaces/wb/fby/settings" },
            ],
          },
        ],
      },
      yandex: {
        name: "Yandex",
        icon: <YandexIcon />,
        subItems: [
          { name: "Настройки", path: "/modules/marketplaces/yandex/settings" },
          { name: "Заказы FBS", path: "/modules/marketplaces/fbs/orders" },
          {
            name: "FBY",
            children: [{ name: "Поставки", path: "/modules/marketplaces/yandex/fby/supply" }],
          },
        ],
      },
      moysklad: {
        name: "МойСклад",
        icon: <MoyskladIcon />,
        subItems: [{ name: "Настройки", path: "/modules/marketplaces/moysklad/settings" }],
      },
      "ai-memory": {
        name: "ИИ",
        icon: <BoxCubeIcon />,
        subItems: [
          { name: "Анализ", path: "/modules/ai-memory/insights" },
          { name: "Настройки", path: "/modules/ai-memory/settings" },
        ],
      },
    };
    for (const moduleName of enabledModuleOrder) {
      const nav = knownModuleItems[moduleName];
      if (nav) dynamicItems.push(nav);
      if (moduleName === "marketplaces") {
        dynamicItems.push(
          knownModuleItems.ozon,
          knownModuleItems.wb,
          knownModuleItems.yandex,
          knownModuleItems.moysklad
        );
      }
    }

    if (canManageUsers) {
      dynamicItems.push({
        name: "Пользователи",
        icon: <GroupIcon />,
        subItems: [
          { name: "Добавить", path: "/modules/users/add" },
          { name: "Список", path: "/modules/users/list" },
        ],
      });
    }

    if (isAdmin) {
      dynamicItems.push({
        name: "Админка",
        icon: <GroupIcon />,
        subItems: [{ name: "Тарифы marketplace", path: "/admin/marketplaces/tariffs" }],
      });
      dynamicItems.push({
        name: "Модули",
        icon: <PlugInIcon />,
        subItems: [{ name: "Настройки", path: "/modules/settings" }],
      });
    }

    return dynamicItems;
  }, [enabledModuleOrder, isAdmin, canManageUsers]);

  useEffect(() => {
    let alive = true;

    const loadEnabledModules = async () => {
      try {
        const base = getGatewayBaseUrl();
        const token = getToken();
        setIsAdmin(hasAdminAccess(token));
        const headers = token ? { authorization: `Bearer ${token}` } : {};
        const metaResp = await fetch(`${base}/plugins/_meta?enabled_only=true`, {
          cache: "no-store",
          headers,
        });
        if (!metaResp.ok) {
          throw new Error(`plugins meta failed: ${metaResp.status}`);
        }
        const data = (await metaResp.json()) as PluginMeta[];
        let usersManage = false;
        if (token) {
          const usersManageResp = await fetch(`${base}/plugins/access/check/users.manage`, {
            cache: "no-store",
            headers,
          });
          usersManage = usersManageResp.ok
            ? (((await usersManageResp.json()) as AccessCheckResponse).allowed ?? false)
            : false;
        }
        if (!alive) return;
        const names = (data || []).filter((item) => item.enabled).map((item) => item.name);
        setCanManageUsers(usersManage);
        setEnabledModuleOrder(names);
      } catch {
        if (alive) {
          setEnabledModuleOrder([]);
          setCanManageUsers(false);
        }
      }
    };

    void loadEnabledModules();
    const timer = window.setInterval(() => {
      void loadEnabledModules();
    }, 15000);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const renderMenuItems = (
    navItems: NavItem[],
    menuType: "main" | "others"
  ) => (
    <ul className="flex flex-col gap-2">
      {navItems.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group  ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={` ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                } ${nav.badgeCount ? "relative" : ""}`}
              >
                {nav.icon}
                {nav.badgeCount ? (
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-white bg-red-500 dark:border-gray-900" />
                ) : null}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <>
                  <span className={`menu-item-text`}>{nav.name}</span>
                  {nav.badgeCount ? (
                    <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white">
                      {nav.badgeCount > 99 ? "99+" : nav.badgeCount}
                    </span>
                  ) : null}
                </>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200  ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                href={nav.path}
                onClick={(event) => handleMenuLinkClick(event, nav.path)}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className={`menu-item-text`}>{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul
                ref={(el) => {
                  subMenuContentRefs.current[`${menuType}-${index}`] = el;
                }}
                className="mt-2 space-y-1 ml-9"
              >
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    {subItem.path ? (
                      <Link
                        href={subItem.path}
                        onClick={(event) => handleMenuLinkClick(event, subItem.path)}
                        className={`menu-dropdown-item ${
                          isActive(subItem.path)
                            ? "menu-dropdown-item-selected"
                            : "menu-dropdown-item-inactive"
                        }`}
                      >
                        {subItem.name}
                        <span className="flex items-center gap-1 ml-auto">
                          {subItem.new && (
                            <span
                              className={`ml-auto ${
                                isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                              } menu-dropdown-badge `}
                            >
                              new
                            </span>
                          )}
                          {subItem.pro && (
                            <span
                              className={`ml-auto ${
                                isActive(subItem.path)
                                  ? "menu-dropdown-badge-active"
                                  : "menu-dropdown-badge-inactive"
                              } menu-dropdown-badge `}
                            >
                              pro
                            </span>
                          )}
                        </span>
                      </Link>
                    ) : (
                      subItem.children?.length ? (
                        (() => {
                          const k = `${menuType}-${index}-${subItem.name}`;
                          const isChildGroupOpen = openSubItemChildren[k] ?? true;
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setOpenSubItemChildren((prev) => ({
                                  ...prev,
                                  [k]: !isChildGroupOpen,
                                }));
                              }}
                              className={`menu-dropdown-item w-full flex items-center ${
                                isChildGroupOpen
                                  ? "menu-dropdown-item-active"
                                  : "menu-dropdown-item-inactive"
                              }`}
                            >
                              {subItem.name}
                              <ChevronDownIcon
                                className={`ml-auto w-4 h-4 transition-transform duration-200 ${
                                  isChildGroupOpen
                                    ? "rotate-180 text-brand-500"
                                    : ""
                                }`}
                              />
                            </button>
                          );
                        })()
                      ) : (
                        <div className="menu-dropdown-item menu-dropdown-item-inactive cursor-default">
                          {subItem.name}
                        </div>
                      )
                    )}
                    {subItem.children?.length ? (
                      (openSubItemChildren[`${menuType}-${index}-${subItem.name}`] ?? true) ? (
                        <ul className="mt-1 space-y-1 ml-4">
                          {subItem.children.map((child) => (
                            <li key={child.name}>
                              {child.path ? (
                                <Link
                                  href={child.path}
                                  onClick={(event) => handleMenuLinkClick(event, child.path!)}
                                  className={`menu-dropdown-item ${
                                    isActive(child.path)
                                      ? "menu-dropdown-item-selected"
                                      : "menu-dropdown-item-inactive"
                                  }`}
                                >
                                  {child.name}
                                </Link>
                              ) : child.children?.length ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const childKey = `${menuType}-${index}-${subItem.name}-${child.name}`;
                                      setOpenSubItemChildren((prev) => ({
                                        ...prev,
                                        [childKey]: !(prev[childKey] ?? true),
                                      }));
                                    }}
                                    className={`menu-dropdown-item w-full flex items-center ${
                                      (openSubItemChildren[`${menuType}-${index}-${subItem.name}-${child.name}`] ?? true)
                                        ? "menu-dropdown-item-active"
                                        : "menu-dropdown-item-inactive"
                                    }`}
                                  >
                                    {child.name}
                                    <ChevronDownIcon
                                      className={`ml-auto w-4 h-4 transition-transform duration-200 ${
                                        (openSubItemChildren[`${menuType}-${index}-${subItem.name}-${child.name}`] ?? true)
                                          ? "rotate-180 text-brand-500"
                                          : ""
                                      }`}
                                    />
                                  </button>
                                  {(openSubItemChildren[`${menuType}-${index}-${subItem.name}-${child.name}`] ?? true) ? (
                                    <ul className="mt-1 space-y-1 ml-4">
                                      {child.children.map((grandChild) => (
                                        <li key={grandChild.name}>
                                          {grandChild.path ? (
                                            <Link
                                              href={grandChild.path}
                                              onClick={(event) => handleMenuLinkClick(event, grandChild.path!)}
                                              className={`menu-dropdown-item ${
                                                isActive(grandChild.path)
                                                  ? "menu-dropdown-item-selected"
                                                  : "menu-dropdown-item-inactive"
                                              }`}
                                            >
                                              {grandChild.name}
                                            </Link>
                                          ) : (
                                            <div className="menu-dropdown-item menu-dropdown-item-inactive cursor-default">
                                              {grandChild.name}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null}
                                </>
                              ) : (
                                <div className="menu-dropdown-item menu-dropdown-item-inactive cursor-default">
                                  {child.name}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : null
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const subMenuContentRefs = useRef<Record<string, HTMLUListElement | null>>({});
  const prevPathnameRef = useRef(pathname);

  // const isActive = (path: string) => path === pathname;
   const isActive = useCallback((path: string) => path === pathname, [pathname]);
  const handleMenuLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
      if (!isActive(path)) return;
      event.preventDefault();
      window.location.reload();
    },
    [isActive]
  );

  useEffect(() => {
    // Check if the current path matches any submenu item
    let submenuMatched = false;
    const hasActiveSubItem = (item: SubItem): boolean =>
      Boolean((item.path && isActive(item.path)) || item.children?.some(hasActiveSubItem));
    const openActiveChildGroups: Record<string, boolean> = {};
    const markActiveChildGroups = (
      menuType: "main" | "others",
      index: number,
      item: SubItem,
      nameChain: string[]
    ) => {
      if (!item.children?.length || !item.children.some(hasActiveSubItem)) return;
      openActiveChildGroups[`${menuType}-${index}-${nameChain.join("-")}`] = true;
      item.children.forEach((child) => {
        if (hasActiveSubItem(child)) {
          markActiveChildGroups(menuType, index, child, [...nameChain, child.name]);
        }
      });
    };

    ["main", "others"].forEach((menuType) => {
      const items = menuType === "main" ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            const hit = hasActiveSubItem(subItem);
            if (hit) {
              setOpenSubmenu({
                type: menuType as "main" | "others",
                index,
              });
              markActiveChildGroups(menuType as "main" | "others", index, subItem, [subItem.name]);
              submenuMatched = true;
            }
          });
        }
      });
    });

    // Keep manually opened menus during sidebar polling; only auto-close on real route change.
    if (!submenuMatched && prevPathnameRef.current !== pathname) {
      setOpenSubmenu(null);
    }
    if (Object.keys(openActiveChildGroups).length) {
      setOpenSubItemChildren((prev) => ({ ...prev, ...openActiveChildGroups }));
    }
    prevPathnameRef.current = pathname;
  }, [pathname, isActive, navItems]);

  useEffect(() => {
    // Set the height of the submenu items when the submenu is opened
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuContentRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuContentRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu, openSubItemChildren]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  return (
    <aside
      className={`fixed top-16 bottom-0 flex shrink-0 flex-col lg:sticky lg:top-0 lg:bottom-auto lg:h-screen px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`shrink-0 py-6 flex  ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link href="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <Image
                className="h-auto w-auto max-h-10 max-w-[160px] dark:hidden"
                src={logoPng}
                alt="Logo"
                width={200}
                height={48}
              />
              <Image
                className="hidden h-auto w-auto max-h-10 max-w-[160px] dark:block"
                src={logoPng}
                alt="Logo"
                width={200}
                height={48}
              />
            </>
          ) : (
            <Image
              src="/images/logo/logo-icon.svg"
              alt="Logo"
              width={32}
              height={32}
            />
          )}
        </Link>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-4">
          <div className="flex flex-col gap-3">
            <div>
              <h2
                className={`mb-2 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? null : (
                  <HorizontaLDots />
                )}
              </h2>
              {renderMenuItems(navItems, "main")}
            </div>

            <div className="">
              {othersItems.length ? (
                <>
                  <h2
                    className={`mb-2 text-xs uppercase flex leading-[20px] text-gray-400 ${
                      !isExpanded && !isHovered
                        ? "lg:justify-center"
                        : "justify-start"
                    }`}
                  >
                    {isExpanded || isHovered || isMobileOpen ? (
                      "Others"
                    ) : (
                      <HorizontaLDots />
                    )}
                  </h2>
                  {renderMenuItems(othersItems, "others")}
                </>
              ) : null}
            </div>
          </div>
        </nav>
      </div>
      <div
        className={`mt-auto shrink-0 pt-3 pb-4 ${
          isExpanded || isHovered || isMobileOpen ? "flex justify-center" : "hidden lg:flex lg:justify-center"
        }`}
      >
        <Image src={meshLogoPng} alt="Mesh logo" width={144} height={34} className="h-auto w-auto max-w-[144px]" />
      </div>
    </aside>
  );
};

export default AppSidebar;
